import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../../services/wss/events/EngineNames';
import templateEngine from '../TemplateEngine';
import { buildHtml } from './IframeRendererHelper';
import userSettingsEngine from '../../../../controllers/UserSettings/UserSettingsEngine';
import userTopicService from '../../../../services/UserTopicService';
import AppContext from '../../../../controllers/AppContext';
import responseHelper from '../../../../services/ResponseHelper';
import MarkdownRenderer from './MarkdownRenderer';
import logService from '../../../../services/wss/logService3';

const _1MB = 1024 * 1024;
const _maxTextLength = 160;
const _name = 'ContentPanelInputRenderer';

export default function ContentPanelInputRenderer({ entry, viewMode }) {
    const iframeRef = useRef(null);

    const rebuildIframe = () => {
        if (!entry?.tid) return;
        const template = templateEngine.getTemplate(entry.tid);
        if (!template) return;
        const dateFormat = userSettingsEngine.resolve('contentPanel.chipDateTimeFormat');
        const settings = {
            dayparts: {
                night:     userSettingsEngine.resolve('contentPanel.daypart.night'),
                morning:   userSettingsEngine.resolve('contentPanel.daypart.morning'),
                afternoon: userSettingsEngine.resolve('contentPanel.daypart.afternoon'),
                evening:   userSettingsEngine.resolve('contentPanel.daypart.evening'),
            },
        };
        const html = buildHtml(template.body, entry.body, templateEngine.getTemplate, entry, template, dateFormat, settings, (format) => templateEngine.getTemplateByFormat(format));
        if (!html) return;
        if (iframeRef.current) iframeRef.current.srcdoc = html;
    };

    const parseBody = () => {
        if (!entry?.body) return null;
        if (new Blob([entry.body]).size > _1MB) return null;
        try { return JSON.parse(entry.body); } catch { return null; }
    };

    const persist = async (body) => {
        const jwt = AppContext.getToken();
        if (!entry?.rk || !jwt) return;
        const serialized = JSON.stringify(body);
        if (new Blob([serialized]).size > _1MB) return;
        const resp = await userTopicService.updateJournalBody(entry.rk, serialized, jwt);
        const code = responseHelper.check(resp, 'persist', _name);
        if (code < 0) logService.logError(`${_name}.persist: response code: ${code}`);
    };

    const handleAppend = (e) => {
        try {
            if (e.source !== iframeRef.current?.contentWindow) return;
            const entry = e.data?.entry;
            if (!entry?.ems) return;
            if (!entry.type || entry.type === 'item') {
                const text = entry.text;
                if (!text || typeof text !== 'string') return;
                if (text.length > _maxTextLength) return;
            }
            if (entry.type === 'meal' || entry.type === 'action') {
                if (!Array.isArray(entry.items) || entry.items.length === 0) return;
            }
            const body = parseBody();
            if (!body) return;
            if (!Array.isArray(body.userInput)) body.userInput = [];
            body.userInput.push(entry);
            persist(body);
        } catch (error) {
            logService.logError(`${_name}.handleAppend: ${error.message}`);
        }
    };

    const handleUpdateMeal = (e) => {
        if (e.source !== iframeRef.current?.contentWindow) return;
        const { ems, items } = e.data ?? {};
        if (!ems || !Array.isArray(items)) return;
        const body = parseBody();
        if (!body) return;
        const item = body.userInput?.find(i => i.ems === ems);
        if (!item || (item.type !== 'meal' && item.type !== 'action')) return;
        item.items = items;
        persist(body);
    };

    const handleUpdate = (e) => {
        if (e.source !== iframeRef.current?.contentWindow) return;
        const { ems, daypart, text, name } = e.data?.entry ?? {};
        if (!ems) return;
        const body = parseBody();
        if (!body) return;
        const item = body.userInput?.find(i => i.ems === ems);
        if (!item) return;
        if (daypart !== undefined) item.daypart = daypart;
        if (text !== undefined) item.text = text;
        if (name !== undefined) item.name = name;
        persist(body);
    };

    const handleUpdateEms = (e) => {
        if (e.source !== iframeRef.current?.contentWindow) return;
        const { oldEms, newEms } = e.data ?? {};
        if (!oldEms || !newEms) return;
        const body = parseBody();
        if (!body) return;
        const item = body.userInput?.find(i => i.ems === oldEms);
        if (!item) return;
        item.ems = newEms;
        persist(body);
    };

    const handleUpdateLayout = (e) => {
        if (e.source !== iframeRef.current?.contentWindow) return;
        const layout = e.data?.layout;
        if (!layout) return;
        const indexNote = templateEngine.getTemplate(entry.tid);
        if (!indexNote) return;
        // Index body parsen voor metaRk
        let metaRk;
        try {
            const index = JSON.parse(indexNote.body);
            metaRk = index.metaRk;
        } catch (_) {
            // Geen index — oude template structuur, niets doen
            return;
        }
        if (!metaRk) return;
        const metaNote = templateEngine.getTemplate(metaRk);
        if (!metaNote) return;
        const jwt = AppContext.getToken();
        if (!jwt) return;
        // layout_config waarde vervangen in meta note body
        // Layout config heeft geen geneste objects — [^}]+ is voldoende
        const updated = metaNote.body.replace(
            /layout_config=\{[^}]+\}/,
            'layout_config=' + JSON.stringify(layout)
        );
        if (updated === metaNote.body) return;
        userTopicService.updateJournalBody(metaRk, updated, jwt);
    };

    const handleRemove = (e) => {
        if (e.source !== iframeRef.current?.contentWindow) return;
        const ems = e.data?.ems;
        if (!ems || typeof ems !== 'number') return;
        const body = parseBody();
        if (!body) return;
        if (!Array.isArray(body.userInput)) return;
        body.userInput = body.userInput.filter((i) => i.ems !== ems);
        persist(body);
    };

    useEffect(() => { rebuildIframe(); }, [entry?.rk, entry?.tid, viewMode]);

    useEffect(() => {
        const handleTemplatesChanged = (e) => {
            if (e.target !== 'templatesChanged') return;
            rebuildIframe();
        };
        const unsub = ComponentNotifier.subscribe(EngineNames.TemplateEngine, handleTemplatesChanged);
        return () => unsub();
    }, [entry?.tid]);

    useEffect(() => {
        const handleMessage = (e) => {
            if (e.data?.type === 'jerny:append') { handleAppend(e); return; }
            if (e.data?.type === 'jerny:update') { handleUpdate(e); return; }
            if (e.data?.type === 'jerny:updatemeal') { handleUpdateMeal(e); return; }
            if (e.data?.type === 'jerny:updateems') { handleUpdateEms(e); return; }
            if (e.data?.type === 'jerny:remove') { handleRemove(e); return; }
            if (e.data?.type === 'jerny:rebuild') { rebuildIframe(); return; }
            if (e.data?.type === 'jerny:clipboard') {
                navigator.clipboard.writeText(e.data.text ?? '').catch(() => {});
                return;
            }
            if (e.data?.type === 'jerny:drop') {
                iframeRef.current?.contentWindow?.postMessage({
                    type: 'jerny:dropresult',
                    ems: e.data.ems,
                    dp: e.data.dp,
                    isCtrlDown: e.data.isCtrlDown,
                }, '*');
                return;
            }
            if (e.data?.type === 'jerny:updatelayout') { handleUpdateLayout(e); return; }
            if (e.data?.type === 'jerny:openurl') {
                const url = e.data?.url;
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }
            // CHANGED — add iframe offset so panel appears at actual mouse position
            if (e.data?.type === 'jerny:contextmenu') {
                const rect = iframeRef.current?.getBoundingClientRect() ?? { left: 0, top: 0 };
                window.dispatchEvent(new CustomEvent('jerny:contextmenu', {
                    detail: { x: rect.left + e.data.x, y: rect.top + e.data.y }
                }));
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [entry?.rk, entry?.body]);

    if (viewMode === 'json') return <MarkdownRenderer entry={entry} viewMode="json" />;
    if (viewMode === 'plain') return <Typography variant="body2" sx={{ color: 'var(--j-text)', whiteSpace: 'pre-wrap', fontSize: 13, p: 1 }}>{entry.body}</Typography>;
    if (viewMode === 'md') return <MarkdownRenderer entry={entry} viewMode="md" />;
    if (!entry?.tid) return <Typography variant="caption" sx={{ color: 'var(--j-muted)', p: 1, display: 'block' }}>No template linked to this entry.</Typography>;
    if (!templateEngine.getTemplate(entry.tid)) return <Typography variant="caption" sx={{ color: 'var(--j-muted)', p: 1, display: 'block' }}>Template not found or not approved yet.</Typography>;

    return (
        <Box id="input-renderer-root" sx={{ width: '100%', height: '100%' }}>
            <iframe ref={iframeRef} sandbox="allow-scripts" style={{ width: '100%', height: '100%', border: 'none', background: '#0b1e2e' }} title="input-renderer" />
        </Box>
    );
}
